import { Code2 } from "lucide-react";
import * as React from "react";
import type { CliScope } from "../lib/cli";
import { applyCommand, fromYaml, pythonSnippet, toYaml, type Manifest } from "../lib/manifest";
import { Textarea } from "../primitives/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../primitives/menu";
import { CopyButton } from "./copy";

function CodeBlock({ code, label }: { code: string; label: string }) {
  return (
    <div className="relative rounded-lg border border-line bg-sunken">
      <div className="absolute top-2 right-2">
        <CopyButton text={code} label={`Copy ${label}`} />
      </div>
      <pre tabIndex={0} aria-label={label} className="overflow-x-auto p-4 pr-12 font-mono text-mono text-fg-secondary">
        {code}
      </pre>
    </div>
  );
}

const VIEWS = { yaml: "YAML", cli: "CLI", python: "Python" } as const;
type View = "form" | keyof typeof VIEWS;

/**
 * Form / YAML / CLI / Python (ui-spec §3.5 item 1, ADR-066): every create flow can show the manifest it will send.
 * The form is the page; one `⋯` menu opens the YAML, CLI and Python views and imports a manifest file. YAML is the
 * source of truth: the form writes into the manifest, edits in YAML replace it (unknown fields kept), and CLI and
 * Python render the same object scoped to the page's org and project.
 */
export function ManifestTabs({
  manifest,
  onManifestChange,
  form,
  scope,
  shortCommand,
  omitScope = false,
}: {
  manifest: Manifest;
  onManifestChange: (m: Manifest) => void;
  form: React.ReactNode;
  scope: CliScope;
  /** The short form (`nodus run --gpu H100 -- python train.py`), when the kind has one. */
  shortCommand?: string;
  omitScope?: boolean;
}) {
  const [view, setView] = React.useState<View>("form");
  const [text, setText] = React.useState(() => toYaml(manifest));
  const [error, setError] = React.useState<string>();
  const editing = React.useRef(false);
  const file = React.useRef<HTMLInputElement>(null);
  const errorId = React.useId();

  React.useEffect(() => {
    if (!editing.current) setText(toYaml(manifest));
  }, [manifest]);

  const parseManifest = (value: string): ReturnType<typeof fromYaml> => {
    const result = fromYaml(value);
    if ("error" in result) return result;
    const next = result.manifest;
    if (next.kind !== manifest.kind || next.apiVersion !== manifest.apiVersion) {
      return { error: `Use a ${manifest.apiVersion} ${manifest.kind} manifest in this form.` };
    }
    if (!next.metadata || Array.isArray(next.metadata) || typeof next.metadata !== "object" || typeof next.metadata.name !== "string") {
      return { error: "The manifest needs metadata.name as a string." };
    }
    if (!next.spec || typeof next.spec !== "object" || Array.isArray(next.spec)) {
      return { error: "The manifest needs a spec object." };
    }
    return result;
  };

  const menu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Manifest views"
          className="inline-flex h-[var(--nd-control-height-sm)] items-center justify-center gap-1.5 px-2 text-small rounded-md text-fg-muted hover:bg-ghost-hover hover:text-fg-primary data-[state=open]:bg-ghost-hover data-[state=open]:text-fg-primary"
        >
          <Code2 className="size-3.5" aria-hidden />Code
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {(Object.keys(VIEWS) as (keyof typeof VIEWS)[]).map((v) => (
          <DropdownMenuItem key={v} disabled={view === v} onSelect={() => setView(v)}>
            {VIEWS[v]}
          </DropdownMenuItem>
        ))}
        <div role="separator" className="-mx-1 my-1 h-px bg-line-subtle" />
        <DropdownMenuItem onSelect={() => file.current?.click()}>Import manifest</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="relative">
      <input
        ref={file}
        type="file"
        accept=".yaml,.yml,application/yaml,text/yaml"
        aria-label="Import manifest"
        className="sr-only"
        tabIndex={-1}
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          const imported = await f.text();
          const r = parseManifest(imported);
          if ("error" in r) {
            setText(imported);
            setError(r.error);
            setView("yaml");
          } else {
            setError(undefined);
            onManifestChange(r.manifest);
          }
          e.target.value = "";
        }}
      />
      {view === "form" ? (
        <>
          <div className="mb-3 flex justify-end">{menu}</div>
          {form}
        </>
      ) : (
        <>
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-ui">
              <button type="button" className="text-fg-muted hover:text-fg-primary" onClick={() => setView("form")}>
                Back to form
              </button>
              <span className="text-fg-muted" aria-hidden>
                /
              </span>
              <span className="text-fg-primary">{VIEWS[view]}</span>
            </div>
            {menu}
          </div>
          {view === "yaml" ? (
            <>
              <Textarea
                aria-label="Manifest YAML"
                aria-invalid={Boolean(error)}
                aria-describedby={error ? errorId : undefined}
                spellCheck={false}
                className="min-h-80 font-mono text-mono"
                value={text}
                onFocus={() => {
                  editing.current = true;
                }}
                onBlur={() => {
                  editing.current = false;
                }}
                onChange={(e) => {
                  setText(e.target.value);
                  const r = parseManifest(e.target.value);
                  if ("error" in r) setError(r.error);
                  else {
                    setError(undefined);
                    onManifestChange(r.manifest);
                  }
                }}
              />
              {error ? (
                <p id={errorId} role="alert" className="mt-2 text-small text-danger-fg">
                  {error}
                </p>
              ) : null}
            </>
          ) : view === "cli" ? (
            <div className="grid gap-3">
              <CodeBlock code={applyCommand(manifest, scope, omitScope)} label="CLI command" />
              {shortCommand ? (
                <>
                  <p className="text-small text-fg-muted">Or the short form:</p>
                  <CodeBlock code={shortCommand} label="Short CLI command" />
                </>
              ) : null}
            </div>
          ) : (
            <CodeBlock code={pythonSnippet(manifest)} label="Python" />
          )}
        </>
      )}
    </div>
  );
}
